import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, defaultIfEmpty, filter, first, forkJoin, map, Observable, of, switchMap, withLatestFrom } from 'rxjs';

import {
  ChosenCompositeUtils,
  ChosenCorrespondentMailData,
  ChosenCorrespondentMailType,
  CustomerService,
  FormFacade,
  Formly,
  MAIL_SEND_FORMLY,
  MailRecipient,
  MailRecipientsFormComponent,
  unmarshal,
  UntilDestroy,
  untilDestroyed,
  UserUtil,
  isObject,
  isString,
} from '@contact/upgrade';

enum RecipientsFormField {
  RECIPIENTS = 'RECIPIENTS',
}

@UntilDestroy()
@Component({
  selector: 'cng-desktop-mail-recipients',
  templateUrl: 'desktop-mail-recipients.component.html',
  styleUrls: ['./desktop-mail-recipients.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAIL_SEND_FORMLY,
      useClass: Formly,
    },
  ],
})
export class DesktopMailRecipientsComponent implements OnInit, AfterViewInit {
  @Input() request: any;
  @Input() initialContact: MailRecipient;
  @Input() id: string;

  // formly
  readonly formlyForm = this.formly.form;
  readonly formlyData$ = this.formly.data$;
  readonly formlyValid$ = this.formly.valid$;

  readonly FormField = RecipientsFormField;

  // must use ViewChildren instead of ViewChild because the child component is not accessible from the beginning (ngIf)
  @ViewChildren('recipients') public recipients: QueryList<MailRecipientsFormComponent>;

  private _recipientsLoaded$ = new BehaviorSubject<boolean>(false);
  readonly recipientsLoaded$ = this._recipientsLoaded$.pipe(first(is => is));

  constructor(
    @Inject(MAIL_SEND_FORMLY) private readonly formly: Formly,
    private readonly formFacade: FormFacade,
    private readonly customerService: CustomerService
  ) {}

  ngOnInit() {
    const id = this.id ?? `${this.request.getId()}`;
    this.formFacade
      .getData(id)
      .pipe(first(), untilDestroyed(this))
      .subscribe(formData => {
        // set formly data
        this.formly.setId(id);

        let initialData: { to?: ChosenCorrespondentMailData; cc?: ChosenCorrespondentMailData; bcc?: ChosenCorrespondentMailData };
        if (formData) {
          // if there's an existing form in the store it means the form has been closed "temporarily" and we can take all the data from the store
          const oldFormData = MailRecipientsFormComponent.getFormData(formData);
          initialData = { to: oldFormData.to, cc: oldFormData.cc, bcc: oldFormData.bcc };
        } else {
          // else we get the recipients' data from the frame
          initialData = this.request.getRecipientFields();
          // and if there are no recipient in the frame we take it from the @Input (outgoing emails)
          if ((!initialData || !initialData.to || initialData.to.length == 0) && this.initialContact && this.initialContact.email) {
            initialData = { to: [this.initialContact.email] };
          }
        }
        // we take the initial data, pipe it in the "recipient -> ChosenCorrespondentMailData" pipe, and initialise the form with it
        of([initialData.to, initialData.cc, initialData.bcc])
          .pipe(
            switchMap(([to, cc, bcc]) => {
              return this.getChosenDataFromAddresses(to, cc, bcc);
            })
          )
          .subscribe(recipients => {
            this.initFormly(recipients.to || [], recipients.cc || [], recipients.bcc || []);
            this._recipientsLoaded$.next(true);

            // watches the changes of the form and sends them to the frame
            this.formlyData$.pipe(untilDestroyed(this), withLatestFrom(this.formlyValid$)).subscribe(([data, isValid]) => {
              if (data) {
                const recipients = unmarshal<any>(data[this.FormField.RECIPIENTS]) || {};
                const tos = (recipients && recipients.to) || [];
                const ccs = (recipients && recipients.cc) || [];
                const bccs = (recipients && recipients.bcc) || [];
                this.request.setRecipientFields({
                  to: tos.map(to => (isString(to) ? to : to.value.email)),
                  cc: ccs.map(cc => (isString(cc) ? cc : cc.value.email)),
                  bcc: bccs.map(bcc => (isString(bcc) ? bcc : bcc.value.email)),
                  valid: isValid,
                });
              }
            });
          });
      });
    // watches the "reply all" action and changes the form accordingly
    (this.request.replyAllRecipients$ as Observable<any>)
      .pipe(
        filter(recipients => !!recipients),
        switchMap(recipients => {
          const existing = this.formlyForm.value[this.FormField.RECIPIENTS];
          const newTos = this.ensureUnicity(existing.to, recipients.to);
          const newCcs = this.ensureUnicity(existing.cc, recipients.cc);
          return this.getChosenDataFromAddresses(newTos, newCcs);
        }),
        untilDestroyed(this)
      )
      .subscribe(recipients => {
        const existing = this.formlyForm.value[this.FormField.RECIPIENTS];
        this.formlyForm.setValue({
          [this.FormField.RECIPIENTS]: {
            to: existing.to.concat(recipients.to || []),
            cc: existing.cc.concat(recipients.cc || []),
            bcc: existing.bcc, //no changes on BCCs here
          },
        });
      });
    // watch the "frame removed" event to empty the form store
    this.request.removed$.pipe(untilDestroyed(this)).subscribe(_ => {
      // empty the store when the request is destroyed
      this.formFacade.remove('' + this.request.getId());
    });
    this.request.updateRecipients$.pipe(untilDestroyed(this)).subscribe(recipient => {
      const existing = this.formlyForm.value[this.FormField.RECIPIENTS];
      let oldEmails = recipient.oldEmail.split(';');

      const toIndex = existing.to.findIndex(d =>
        isObject(d) ? oldEmails.findIndex(e => d.value.email === e) !== -1 : oldEmails.findIndex(e => d === e) !== -1
      );
      const toFiltered = existing.to.filter(d =>
        isObject(d) ? oldEmails.findIndex(e => d.value.email === e) !== -1 : oldEmails.findIndex(e => d === e) !== -1
      );

      let toField: ChosenCorrespondentMailType | null = null;
      if (toIndex !== -1) {
        if (isString(existing.to[toIndex])) {
          toField = recipient.newEmail;
        } else {
          toField = MailRecipientsFormComponent.toChosenData({
            type: existing.to[toIndex].type ?? 'contact',
            id: existing.to[toIndex].value.id ?? '',
            firstname: existing.to[toIndex].value.firstname,
            lastname: existing.to[toIndex].value.lastname,
            email: recipient.newEmail ?? '',
          });
        }
      }

      this.formlyForm.setValue({
        [this.FormField.RECIPIENTS]: {
          to: [toField].concat(toFiltered || []),
          cc: existing.cc,
          bcc: existing.bcc,
        },
      });
    });
  }

  ngAfterViewInit() {
    // the viewChildren will change only once when the MailRecipientsFormComponent is finally loaded, and it will always contain 1 component
    this.recipients.changes.subscribe(children => {
      let recipientsComponent = children.first as MailRecipientsFormComponent;
      if (recipientsComponent && this.initialContact && this.initialContact.email) {
        const chips$ = recipientsComponent.chosenTo.chosen$.pipe(
          switchMap(({ chosen$ }) => chosen$),
          switchMap(({ chips$ }) => chips$)
        );
        chips$.pipe(untilDestroyed(this)).subscribe(chips =>
          chips.forEach(chip => {
            if (ChosenCompositeUtils.flat(chip.value, 'email') === this.initialContact.email) {
              chip.removable = false;
              chip.disabled = true;
            }
          })
        );
      }
    });
  }

  getChosenDataFromAddresses(
    to?: ChosenCorrespondentMailData,
    cc?: ChosenCorrespondentMailData,
    bcc?: ChosenCorrespondentMailData
  ): Observable<{ to?: ChosenCorrespondentMailData; cc?: ChosenCorrespondentMailData; bcc?: ChosenCorrespondentMailData }> {
    const toJoin: {
      to?: Observable<ChosenCorrespondentMailData>;
      cc?: Observable<ChosenCorrespondentMailData>;
      bcc?: Observable<ChosenCorrespondentMailData>;
    } = {};
    if (to && to.length > 0) toJoin.to = forkJoin([...to.map(data => this.getChosenData(data))]).pipe(defaultIfEmpty([]));
    if (cc && cc.length > 0) toJoin.cc = forkJoin([...cc.map(data => this.getChosenData(data))]).pipe(defaultIfEmpty([]));
    if (bcc && bcc.length > 0) toJoin.bcc = forkJoin([...bcc.map(data => this.getChosenData(data))]).pipe(defaultIfEmpty([]));
    return forkJoin(toJoin).pipe(defaultIfEmpty({}));
  }

  /**
   * transforms Chosen data
   * - if string : attempts to fetch teh customer from els
   * - if composite type, returns as is.
   * @param data the Chosen data
   */
  getChosenData(data: ChosenCorrespondentMailType): Observable<ChosenCorrespondentMailType> {
    if (typeof data == 'string') {
      return this.customerService.searchByAddressUri({ uri: data }).pipe(
        map(contactDoc => {
          if (!contactDoc) return MailRecipientsFormComponent.mailRecipientToChosenData({ type: 'email', email: data });
          else
            return {
              type: 'contact',
              value: {
                id: contactDoc.contactId,
                firstname: contactDoc.contactFirstName,
                lastname: contactDoc.contactLastName,
                name:
                  UserUtil.fullname({
                    firstname: contactDoc.contactFirstName,
                    lastname: contactDoc.contactLastName,
                  }) ?? '',
                fullname: UserUtil.fullname({
                  firstname: contactDoc.contactFirstName,
                  lastname: contactDoc.contactLastName,
                }),
                email: data,
                directory: undefined,
                team: undefined,
              },
            };
        })
      );
    } else {
      // the chosen data is already a composite we do not have to go fetch it
      return of(data);
    }
  }

  /**
   * Filters an array of email addressse by removing every address that already exitst in a given array of ChosenCompositeData
   * @param existingChosen
   * @param newAddresses
   */
  ensureUnicity(existingChosen: ChosenCorrespondentMailData, newAddresses: string[] | undefined) {
    if (!newAddresses) return [];
    if (!existingChosen) return newAddresses;
    const existingAddresses = existingChosen.map(add => (typeof add == 'string' ? add : add.value.email));
    return newAddresses.filter(add => existingAddresses.indexOf(add) == -1);
  }

  private initFormly(to?: ChosenCorrespondentMailData, cc?: ChosenCorrespondentMailData, bcc?: ChosenCorrespondentMailData) {
    this.formly.init(
      {
        [this.FormField.RECIPIENTS]: new FormControl({ to, cc, bcc }),
      },
      null,
      null,
      {}
    );
  }
}
